import { areaForPathname } from "@/features/internal-user/area-routes";
import { AreaAccess, CrmArea } from "@/features/internal-user/services/access";
import { PermissionType } from "@/helpers/role-permission";
import { ReactNode, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { usePermissionContext } from "./permission-context";

/**
 * The area of the page currently on screen.
 *
 * This mirrors how the server decides: there, the area comes from the controller
 * the route lives on; here, from the page the control is rendered on. Deriving it
 * rather than annotating every gate is what stops the two drifting — a button
 * whose area was typed by hand can be wrong, and a wrong one is invisible until
 * someone is either locked out or wrongly let in.
 *
 * An unmapped page yields undefined, and the gate then behaves exactly as it did
 * before areas existed: permission only. The server still refuses the call.
 */
function useRouteArea(): CrmArea | undefined {
  const scoped = useContext(AreaScopeContext);
  const { pathname } = useLocation();
  return scoped ?? areaForPathname(pathname);
}

const AreaScopeContext = createContext<CrmArea | undefined>(undefined);

/**
 * Pins the area for everything rendered inside, overriding the URL.
 *
 * For the surfaces that travel: the User Details drawer opens from Sessions,
 * Couples Pack and Audit pages, but the actions inside it hit the Users
 * controllers, so the server judges them as Users. Without a scope the gates
 * would judge them by the page underneath — Sessions Edit would show Users
 * buttons that 403 on click. One wrapper at the drawer root fixes every gate
 * inside; a control that acts on yet another area names it with the `area`
 * prop, which still wins over the scope.
 */
export function AreaScope({
  area,
  children,
}: {
  area: CrmArea;
  children: ReactNode;
}) {
  return (
    <AreaScopeContext.Provider value={area}>{children}</AreaScopeContext.Provider>
  );
}

/** What the decision needs — the shape PermissionContext provides. */
export type AccessInputs = {
  permissions: PermissionType[];
  areas: Record<CrmArea, AreaAccess>;
  isSuper: boolean;
  isLoaded?: boolean;
};

/**
 * THE access rule, as a pure function, so every reader — the <HasAccess> gate,
 * the sidebar, anything else — asks the same question the server asks:
 *
 *   1. area  — the role's grant for `area` must allow the capability
 *               (`read` asks for View, any other verb asks for Edit);
 *               skipped for a super role, and when there is no area.
 *   2. verb  — the role must hold `permission`.
 *
 * Both must hold, so the grid can only ever narrow. Never special-case a role
 * name here, and never treat is_super as a permission: is_super skips the
 * area, nothing more. A new role gets an option by being given the verb.
 *
 * False while access is still loading, so callers fail closed.
 */
export function hasAccess(
  ctx: AccessInputs,
  permission: PermissionType,
  area?: CrmArea,
): boolean {
  if (!ctx.isLoaded) return false;

  if (area && !ctx.isSuper) {
    const capability = permission === "read" ? "view" : "edit";
    const grant = ctx.areas[area];
    if (!grant?.[capability]) return false;
  }

  return ctx.permissions.includes(permission);
}

/**
 * `hasAccess` for the page on screen — as a boolean, for the places a wrapper
 * component cannot reach: disabling a form's fields, choosing which footer to
 * render, deciding a drawer's mode. The area defaults to the current route.
 */
export function useHasAccess(
  permission: PermissionType,
  area?: CrmArea,
): boolean {
  const ctx = usePermissionContext();
  const routeArea = useRouteArea();
  return hasAccess(ctx, permission, area ?? routeArea);
}

type HasAccessProps = {
  permission: PermissionType;
  /**
   * Override the area this gate checks. Only needed when a control acts on a
   * different area than the page it sits on; otherwise the area is taken from
   * the current route, which is what keeps ~95 existing call sites correct
   * without each one having to name its area.
   */
  area?: CrmArea;
  fallback?: ReactNode;
  children: ReactNode;
};

export function HasAccess({
  permission,
  area,
  fallback = null,
  children,
}: HasAccessProps) {
  const allowed = useHasAccess(permission, area);
  return <>{allowed ? children : fallback}</>;
}

/**
 * Super-role-only gate, for the handful of controls that are not about an area
 * at all — copying scheduling links, editing another person's Exotel number.
 *
 * Reads the server's answer (/internal/access/me), the same store RolesGuard
 * enforces, rather than Clerk metadata: the two can disagree the moment a role
 * changes, and the server is the one that decides. Fails closed while loading.
 */
export function useIsSuperAdmin() {
  const { isSuper, isLoaded } = usePermissionContext();
  return { isSuperAdmin: !!isLoaded && isSuper, isLoaded: !!isLoaded };
}
